/**
 * Утилиты разбора query-параметров для предзаполнения формы оплаты.
 *
 * Ссылки вида `?pay=1&accountNumber=12345678&amount=500&annual=1`
 * формирует Telegram-бот клиента. Невалидные значения не вызывают ошибок:
 * соответствующее поле просто остаётся пустым (или значением по умолчанию).
 */

import { config } from '../config';

/** Данные предзаполнения формы оплаты из URL. */
export interface PaymentPrefill {
  /** Открыть модальное окно оплаты автоматически. */
  open: boolean;
  /** Номер лицевого счёта (только валидный, иначе отсутствует). */
  accountNumber?: string;
  /** Сумма платежа в рублях (только валидная, иначе отсутствует). */
  amount?: number;
  /** Признак годового платежа. */
  annual: boolean;
}

/**
 * Проверяет значение параметра номера лицевого счёта.
 *
 * Значение обрезается по краям (trim) и сверяется с
 * `config.payment.accountNumberPattern`.
 *
 * @param value - Значение query-параметра (может отсутствовать)
 * @returns true, если после trim значение соответствует формату номера ЛС
 *
 * @example
 * isValidAccountNumberParam(' 12345678 '); // true
 * isValidAccountNumberParam('abc'); // false
 * isValidAccountNumberParam(null); // false
 */
export const isValidAccountNumberParam = (value: string | null | undefined): boolean => {
  if (typeof value !== 'string') {
    return false;
  }
  return config.payment.accountNumberPattern.test(value.trim());
};

/**
 * Разбирает значение суммы из query-параметра.
 *
 * Сумма считается валидной, если `Number(value)` конечно, больше нуля
 * (после округления до 2 знаков) и не превышает `config.payment.annualMaxAmount`.
 * Пустая строка и строка из пробелов — отсутствие значения.
 *
 * @param value - Значение query-параметра (может отсутствовать)
 * @returns Сумма, округлённая до 2 знаков, или undefined для невалидного значения
 */
const parseAmountParam = (value: string | null): number | undefined => {
  if (value === null || value.trim() === '') {
    return undefined;
  }

  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return undefined;
  }

  const rounded = Math.round(parsed * 100) / 100;
  if (rounded <= 0 || rounded > config.payment.annualMaxAmount) {
    return undefined;
  }

  return rounded;
};

/**
 * Разбирает query-параметры предзаполнения формы оплаты.
 *
 * Правила:
 * - `accountNumber`: trim; сохраняется только при совпадении с
 *   `config.payment.accountNumberPattern`, иначе отсутствует;
 * - `amount`: `Number(value)`, только конечное > 0 и не больше
 *   `config.payment.annualMaxAmount`, округление до 2 знаков;
 *   иначе отсутствует (форма оставит значение по умолчанию);
 * - `annual`: `annual === '1'`;
 * - `open`: `pay === '1'` или задан валидный `accountNumber`.
 *
 * @param params - Query-параметры URL
 * @returns Данные предзаполнения формы оплаты
 *
 * @example
 * parsePaymentPrefill(new URLSearchParams('accountNumber=12345678&amount=500.456&annual=1'));
 * // { open: true, accountNumber: '12345678', amount: 500.46, annual: true }
 */
export const parsePaymentPrefill = (params: URLSearchParams): PaymentPrefill => {
  const rawAccountNumber = params.get('accountNumber');
  const accountNumber = isValidAccountNumberParam(rawAccountNumber)
    ? (rawAccountNumber as string).trim()
    : undefined;

  const amount = parseAmountParam(params.get('amount'));
  const annual = params.get('annual') === '1';
  const open = params.get('pay') === '1' || accountNumber !== undefined;

  const prefill: PaymentPrefill = { open, annual };
  if (accountNumber !== undefined) {
    prefill.accountNumber = accountNumber;
  }
  if (amount !== undefined) {
    prefill.amount = amount;
  }
  return prefill;
};
